import { formatNumber } from '@progress/kendo-intl';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { ExcelExport, ExcelExportColumn as ExportColumn } from '@progress/kendo-react-excel-export';
import { Grid, GridColumn as Column, GridCustomCellProps, GridEvent, GridToolbar, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { downloadIcon, filterClearIcon } from '@progress/kendo-svg-icons';
import { RightAlignHeaderCell } from 'components/cells/RightAlignHeaderCell';
import dayjs from 'dayjs';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import DateOnlyCell from '../../components/cells/DateOnlyCell';
import CenterDivPanel from '../../components/CenterDivPanel';
import SimpleDateRangePicker from '../../components/SimpleDateRangePicker';
import useAlert from '../../components/useAlert';
import useConfirm from '../../components/useConfirm';
import { fetchApi } from '../../services/api';
import { openWindowPerc } from '../../services/openWindow';
import { ApiLink } from '../../TypeGen/api-link';
import CarrierAutoComplete from '../AssetCarriers/CarrierAutoComplete';
import DriverAutoComplete from '../AssetDrivers/DriverAutoComplete';
import VehicleAutoComplete from '../AssetVehicles/VehicleAutoComplete';

export const ClaimStatuses = [
  { ID: null, Name: 'Any' },
  { ID: 0, Name: 'Open' },
  { ID: 1, Name: 'In Progress' },
  { ID: 2, Name: 'Closed' }
];
const ClaimTypes = [
  { ID: null, Name: 'Any' },
  { ID: 'Cargo', Name: 'Cargo' },
  { ID: 'Auto Liability', Name: 'Auto Liability' },
  { ID: 'Property Damage', Name: 'Property Damage' },
];

const FreightClaimsContext = createContext({});

type FreightClaimListingRecord = {
  ClaimID: number;
  ClaimStatus: number;
  ClaimType: string;
  ClaimAmount: number;
  CarrierID?: number;
  CarrierName?: string;
  CarrierLink?: ApiLink;
  VehicleID?: number;
  VehicleName?: string;
  VehicleLink?: ApiLink;
  DriverID?: number;
  DriverName?: string;
  DriverLink?: ApiLink;
  OrderID?: number;
  OrderNumber?: number;
  OrderLink?: ApiLink;
  CustomerClaimNumber?: string;
  InsuranceClaimNumber?: string;
  ThirdPartyName?: string;
  ThirdPartyContact?: string;
  ThirdPartyPhone?: string;
  FormalDemand?: number;
  ApprovedAmount?: number;
  Deductible?: number;
  OpenDate?: string;
  ClosedDate?: string;
  LossDate?: string;
  JurisdictionState?: string;
  FollowUpDate?: string;
  FollowUpReason?: string;
  LastStatusUpdate?: string;
  AdjusterName?: string;
  AdjusterPhone?: string;
  AdjusterEmail?: string;
  InsurerGroup?: string;
  PolicyNumber?: string;
  Description?: string;
};

type PostDataFilter = {
  ClaimStatus: number | null;
  ClaimType: string;
  CarrierID?: number;
  VehicleID?: number;
  DriverID?: number;
  OrderNumber?: number;
  FromOpenDate: Date;
  ToOpenDate: Date;
  CustomerClaimNumber?: string;
  InsuranceClaimNumber?: string;
  JurisdictionState?: string;
};

type PostDataState = {
  skip: number;
  take: number;
  sort: string;
  sortDir: 'asc' | 'desc';
  filters: PostDataFilter;
};

const PageSize = 50;

const FreightClaims = () => {
  const render = useRef(true);
  const defaultPostDataState: PostDataState = {
    skip: 0,
    take: PageSize,
    sort: 'OpenDate',
    sortDir: 'desc',
    filters: {
      ClaimStatus: null,
      ClaimType: null,
      CarrierID: null,
      VehicleID: null,
      DriverID: null,
      OrderNumber: null,
      FromOpenDate: dayjs().add(-3, 'month').startOf('day').toDate(),
      ToOpenDate: dayjs().endOf('day').toDate(),
      CustomerClaimNumber: '',
      InsuranceClaimNumber: '',
      JurisdictionState: ''
    }
  };


  const [postDataState, setPostDataState] = useState<PostDataState>(defaultPostDataState);
  const totalRecords = useRef(0);
  const { alert } = useAlert();
  const { ConfirmationDialog } = useConfirm({});
  const _export = useRef<ExcelExport | null>(null);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<FreightClaimListingRecord[]>([]);

  const debouncedFetchRecords = useDebouncedCallback((append = false, _postDataState: PostDataState = null) => {
    fetchRecords(append, _postDataState);
  }, 750);

  const fetchRecords = useCallback((append: boolean, _postDataState: PostDataState = null) => {
    setLoading(true);
    const dataGridState = _postDataState == null ? { ...postDataState } : { ..._postDataState };
    if (!append) {
      dataGridState.take = PageSize;
      dataGridState.skip = 0;
      //document.querySelector('.FreightClaimsListing-andFilter .k-grid-content')?.scrollTo(0, 0);
      setPostDataState(dataGridState);
    }
    fetchApi('/api/Asset/FreightClaimsListing', dataGridState, 'POST')
      .then(({ Data, Total }) => {
        totalRecords.current = Total;
        if (!append) {
          setRecords(Data);
        } else {
          setRecords(prevData => prevData.concat(Data));
        }
        setLoading(false);
      }).catch(async e => {
        setLoading(false);
        if (!e?.status) await alert('Error: Please see admin');
        else if (e.status !== 404) await alert(e?.detail);
      });
  }, [alert, postDataState]);

  useEffect(() => {
    if (render.current === true) {
      render.current = false;
    } else {
      return;
    }
    debouncedFetchRecords(false);
  }, [postDataState.filters, postDataState.sort, debouncedFetchRecords]);


  const scrollHandler = (event: GridEvent) => {
    const scrollElement = event.syntheticEvent.currentTarget as HTMLElement;
    const isNearBottom = scrollElement.scrollHeight > scrollElement.clientHeight
      && scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;

    if (!isNearBottom || loading || records.length >= totalRecords.current)
      return;

    postDataState.skip = records.length;
    fetchRecords(true);
  };

  const SaveAndFetchPostDataState = (o: PostDataState) => {
    setPostDataState(o);
    debouncedFetchRecords(false, o);
  };

  const onSortChange = (event: GridSortChangeEvent) => {
    const sort = event.sort && event.sort.length > 0 ? event.sort[0] : null;
    if (sort) {
      SaveAndFetchPostDataState({ ...postDataState, sort: sort.field as string, sortDir: sort.dir, skip: 0 });
    }
  };

  const StatusCell = (props: GridCustomCellProps) => {
    const status = ClaimStatuses.find(x => x.ID === props.dataItem.ClaimStatus);
    return <td {...props.tdProps}>{status?.Name ?? '-'}</td>;
  };
  const AmountCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps} style={{ textAlign: "right" }}>{formatNumber(props.dataItem.ClaimAmount, "c2")}</td>
  );

  const PartyCell = (props: GridCustomCellProps) => {
    const { CarrierID, CarrierName, CarrierLink, VehicleID, VehicleName, VehicleLink, DriverID, DriverName, DriverLink } = props.dataItem;
    if (CarrierID && CarrierID > 0) {
      return (
        <td {...props.tdProps}>
          {CarrierLink?.Link
            ? <a href="#" style={{ color: "#007bff" }} onClick={(e) => {
              e.preventDefault();
              openWindowPerc(CarrierLink?.Link);
            }}>{CarrierName || CarrierID}</a>
            : (CarrierName || CarrierID)}
        </td>
      );
    } else if ((VehicleID && VehicleID > 0) || (DriverID && DriverID > 0)) {
      return (
        <td {...props.tdProps}>
          {VehicleName &&
            (VehicleLink?.Link
              ? <div>Vehicle: <a href="#" style={{ color: "#007bff" }} onClick={(e) => {
                e.preventDefault();
                openWindowPerc(VehicleLink?.Link);
              }}>{VehicleName}</a></div>
              : <div>Vehicle: {VehicleName}</div>
            )}
          {DriverName &&
            (DriverLink?.Link
              ? <div>Driver: <a href="#" style={{ color: "#007bff" }} onClick={(e) => {
                e.preventDefault();
                openWindowPerc(DriverLink?.Link);
              }}>{DriverName}</a></div>
              : <div>Driver: {DriverName}</div>
            )}
        </td>
      );
    }
    return <td {...props.tdProps}>-</td>;
  };

  const OrderNumberCell = (props: GridCustomCellProps) => {
    const { OrderNumber, OrderLink } = props.dataItem;
    return (
      <td {...props.tdProps}>
        {OrderLink?.Link
          ? <a href="#" style={{ color: "#007bff" }} onClick={(e) => {
            e.preventDefault();
            openWindowPerc(OrderLink.Link);
          }}>{OrderNumber}</a> : '-'}
      </td>
    );
  };

  const FollowUpDateCell = (props: GridCustomCellProps) => {
    if (!props.field || props.rowType !== 'data') return null;
    const dateStr = props.dataItem[props.field];
    const reason = props.dataItem.FollowUpReason;
    if (!dateStr) return <td {...props.tdProps}>N/A</td>;

    const localDate = dayjs.utc(dateStr).local();
    const displayDate = dayjs.utc(dateStr).tz('America/New_York').format('MM/DD/YYYY');
    const style = localDate.isSameOrBefore(dayjs()) ? { color: 'red' } : undefined;

    return <td {...props.tdProps} title={reason ?? ''} style={style}>{displayDate}</td>;
  };

  return (
    <div className="mt-3" style={{ position: "relative" }}>
      {loading && <CenterDivPanel><Loader type="converging-spinner" /></CenterDivPanel>}
      <h4 className="text-left">Cargo Claim Listing</h4>
      <ExcelExport ref={_export} data={records} fileName={`Freight Claims.xlsx`}>
        <ExportColumn field="ClaimID" title="Claim ID" />
        <ExportColumn field="ClaimStatus" title="Claim Status" />
        <ExportColumn field="ClaimType" title="Claim Type" />
        <ExportColumn field="ClaimAmount" title="Claim Amount" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="CarrierName" title="Carrier Name" />
        <ExportColumn field="VehicleName" title="Vehicle Name" />
        <ExportColumn field="DriverName" title="Driver Name" />
        <ExportColumn field="OrderNumber" title="Order Number" />
        <ExportColumn field="CustomerClaimNumber" title="Customer Claim #" />
        <ExportColumn field="InsuranceClaimNumber" title="Insurance Claim #" />
        <ExportColumn field="ThirdPartyName" title="Third Party Name" />
        <ExportColumn field="ThirdPartyContact" title="Third Party Contact" />
        <ExportColumn field="ThirdPartyPhone" title="Third Party Phone" />
        <ExportColumn field="FormalDemand" title="Formal Demand" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="ApprovedAmount" title="Approved Amount" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="Deductible" title="Deductible" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="OpenDate" title="Open Date" />
        <ExportColumn field="ClosedDate" title="Closed Date" />
        <ExportColumn field="LossDate" title="Loss Date" />
        <ExportColumn field="JurisdictionState" title="Jurisdiction" />
        <ExportColumn field="FollowUpDate" title="Follow Up Date" />
        <ExportColumn field="FollowUpReason" title="Follow Up Reason" />
        <ExportColumn field="AdjusterName" title="Adjuster Name" />
        <ExportColumn field="AdjusterPhone" title="Adjuster Phone" />
        <ExportColumn field="AdjusterEmail" title="Adjuster Email" />
        <ExportColumn field="InsurerGroup" title="Insurer Group" />
        <ExportColumn field="PolicyNumber" title="Policy Number" />
        <ExportColumn field="Description" title="Description" />
      </ExcelExport>
      <FreightClaimsContext.Provider value={{}}>
        <Grid
          className='FreightClaimsListing-andFilter'
          scrollable="scrollable"
          style={{ maxHeight: '80vh' }}
          data={records}
          onScroll={scrollHandler}
          resizable={true}
          dataItemKey='ClaimID'
          skip={0}
          take={records.length}
          sortable={{ allowUnsort: false }}
          sort={[{ field: postDataState.sort, dir: postDataState.sortDir }]}
          onSortChange={onSortChange}
          pageable={{ buttonCount: 0, previousNext: false }}
        >
          <GridToolbar>
            <span className="d-flex align-items-center mr-1">
              <label className="mr-1 mb-0">Status:</label>
              <DropDownList
                style={{ width: '160px' }}
                data={ClaimStatuses}
                textField="Name"
                dataItemKey="ID"
                value={ClaimStatuses.find(x => x.ID === postDataState.filters.ClaimStatus)}
                onChange={(e) => {
                  SaveAndFetchPostDataState({ ...postDataState, filters: { ...postDataState.filters, ClaimStatus: e.target.value.ID } });
                }}
              />
            </span>
            <span className="d-flex align-items-center mr-1">
              <label className="mr-1 mb-0">Type:</label>
              <DropDownList
                style={{ width: '160px' }}
                data={ClaimTypes}
                textField="Name"
                dataItemKey="ID"
                value={ClaimTypes.find(x => x.ID === postDataState.filters.ClaimType)}
                onChange={(e) => {
                  SaveAndFetchPostDataState({ ...postDataState, filters: { ...postDataState.filters, ClaimType: e.target.value.ID } });
                }}
              />
            </span>

            <CarrierAutoComplete
              style={{ width: '200px' }}
              selectedCarrierID={postDataState.filters?.CarrierID}
              onSelectedCarrier={(carrier) => {
                SaveAndFetchPostDataState({
                  ...postDataState,
                  filters: { ...postDataState.filters, CarrierID: carrier?.CarrierID ?? null }
                });
              }}
            />

            <VehicleAutoComplete
              style={{ width: '200px' }}
              selectedVehicleID={postDataState.filters?.VehicleID}
              onSelectedVehicle={(vehicle) => {
                SaveAndFetchPostDataState({
                  ...postDataState,
                  filters: { ...postDataState.filters, VehicleID: vehicle?.VehicleID ?? null }
                });
              }}
            />

            <DriverAutoComplete
              style={{ width: '200px' }}
              selectedDriverID={postDataState.filters?.DriverID}
              onSelectedDriver={(driver) => {
                SaveAndFetchPostDataState({
                  ...postDataState,
                  filters: { ...postDataState.filters, DriverID: driver?.DriverID ?? null }
                });
              }}
            />

            <NumericTextBox
              value={postDataState.filters?.OrderNumber}
              placeholder='Order Number'
              min={0}
              spinners={false}
              width={140}
              format="0"
              onChange={(e) => {
                SaveAndFetchPostDataState({
                  ...postDataState,
                  filters: { ...postDataState.filters, OrderNumber: e?.value ?? null }
                });
              }}
            />

            <Button
              title="Clear All Filters and Sort"
              icon="filter-clear"
              svgIcon={filterClearIcon}
              onClick={() => {
                const cleared: PostDataState = {
                  ...defaultPostDataState,
                  filters: {
                    ...defaultPostDataState.filters,
                    FromOpenDate: dayjs().add(-3, 'month').startOf('day').toDate(),
                    ToOpenDate: dayjs().endOf('day').toDate()
                  }
                };
                SaveAndFetchPostDataState(cleared);
              }}
            />
            <Button
              className="ml-1"
              icon="download"
              svgIcon={downloadIcon}
              onClick={() => _export.current && _export.current.save(records)}
            >
              Excel
            </Button>

          </GridToolbar>
          <GridToolbar>
            <span>
              <label className="mt-1">Open Date Range:</label>
              <SimpleDateRangePicker
                value={{
                  start: postDataState.filters.FromOpenDate,
                  end: postDataState.filters.ToOpenDate
                }}
                onChange={(e) => {
                  const newFilters = {
                    ...postDataState.filters,
                    FromOpenDate: dayjs(e.start).startOf('day').toDate(),
                    ToOpenDate: dayjs(e.end).endOf('day').toDate()
                  };
                  const newState = { ...postDataState, filters: newFilters };
                  setPostDataState(newState);
                  if (e && e.start && e.end) {
                    debouncedFetchRecords(false, newState);
                  }
                }}
              />
            </span>
            {/*<Button*/}
            {/*  themeColor="primary"*/}
            {/*  svgIcon={plusIcon}*/}
            {/*  className="ml-2"              */}
            {/*  onClick={() => {*/}
            {/*    setDialogOrderId(null);*/}
            {/*    setShowDialog(true);*/}
            {/*  }}*/}
            {/*>*/}
            {/*  Add Cargo Claim*/}
            {/*</Button>*/}
          </GridToolbar>
          <Column field="ClaimID" title="Claim ID" sortable cells={{
            data: (props) => {
              const claimId = props.dataItem.ClaimID;
              return (
                <td {...props.tdProps}>
                  <Link style={{ color: "#007bff" }} to={`/Assets/FreightClaim/${claimId}`}>
                    {claimId}
                  </Link>
                </td>
              );
            }
          }}
          />
          <Column field="Party" title="Carrier / Vehicle / Driver" sortable={false} cells={{ data: PartyCell }} />
          <Column field="ClaimStatus" title="Status" sortable={false} cells={{ data: StatusCell }} />
          <Column field="ClaimType" title="Type" sortable={false} />
          <Column field="ClaimAmount" title="Amount" sortable={false} cells={{ data: AmountCell, headerCell: RightAlignHeaderCell }} />
          <Column field="OrderNumber" title="Order Number" sortable={false} cells={{ data: OrderNumberCell }} />
          <Column field="CustomerClaimNumber" title="Customer Claim #" sortable={false} />
          <Column field="InsuranceClaimNumber" title="Insurance Claim #" sortable={false} />
          <Column field="OpenDate" title="Open Date" sortable cells={{ data: DateOnlyCell }} />
          <Column field="ClosedDate" title="Closed Date" sortable cells={{ data: DateOnlyCell }} />
          <Column field="LossDate" title="Loss Date" sortable cells={{ data: DateOnlyCell }} />
          <Column field="JurisdictionState" title="Jurisdiction" hidden={true} />
          <Column field="FollowUpDate" title="Follow Up Date" sortable cells={{ data: FollowUpDateCell }} />
          <Column field="FollowUpReason" title="Follow Up Reason" hidden={true} />
          <Column field="CarrierID" hidden={true} />
          <Column field="CarrierName" title="Carrier" hidden={true} />
          <Column field="VehicleID" hidden={true} />
          <Column field="VehicleName" title="Vehicle" hidden={true} />
          <Column field="DriverID" hidden={true} />
          <Column field="DriverName" title="Driver" hidden={true} />
          <Column field="OrderID" hidden={true} />
          <Column field="ThirdPartyName" title="Third Party Name" hidden={true} />
          <Column field="ThirdPartyContact" title="Third Party Contact" hidden={true} />
          <Column field="ThirdPartyPhone" title="Third Party Phone" hidden={true} />
          <Column field="FormalDemand" title="Formal Demand" hidden={true} />
          <Column field="ApprovedAmount" title="Approved Amount" hidden={true} />
          <Column field="Deductible" title="Deductible" hidden={true} />
          <Column field="LastStatusUpdate" title="Last Status Update" hidden={true} />
          <Column field="AdjusterName" title="Adjuster Name" hidden={true} />
          <Column field="AdjusterPhone" title="Adjuster Phone" hidden={true} />
          <Column field="AdjusterEmail" title="Adjuster Email" hidden={true} />
          <Column field="InsurerGroup" title="Insurer Group" hidden={true} />
          <Column field="PolicyNumber" title="Policy Number" hidden={true} />
          <Column field="Description" title="Description" hidden={true} />
        </Grid>
      </FreightClaimsContext.Provider>
      <ConfirmationDialog />
      {/*{showDialog && (*/}
      {/*  <FreightClaimDialog*/}
      {/*    OrderID={dialogOrderId ?? 0}*/}
      {/*    CloseDialog={() => setShowDialog(false)}*/}
      {/*    onSave={handleClaimSaved}*/}
      {/*  />*/}
      {/*)}*/}

    </div>
  );
}

export default FreightClaims;
